// Vocabulary Data Index
// Exports all vocabulary items for Multiple Choice game mode

import { chineseBeginnerItems } from './chinese/beginner';
import { chineseIntermediateItems } from './chinese/intermediate';
import { chineseAdvancedItems } from './chinese/advanced';
import { russianBeginnerItems } from './russian/beginner';
import { russianIntermediateItems } from './russian/intermediate';
import { russianAdvancedItems } from './russian/advanced';
import { germanBeginnerItems } from './german/beginner';
import { germanIntermediateItems } from './german/intermediate';
import { germanAdvancedItems } from './german/advanced';
import { latinBeginnerItems } from './latin/beginner';
import { latinIntermediateItems } from './latin/intermediate';
import { latinAdvancedItems } from './latin/advanced';
import {
  SupportedLanguage,
  VocabularyLevel,
  MultipleChoiceDictionary,
  QuestionType,
  VocabularyItem
} from '../../utils/multipleChoiceLogic';

// Chinese Dictionaries
export const chineseVocabulary: Record<VocabularyLevel, MultipleChoiceDictionary> = {
  beginner: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'chinese',
      level: 'beginner',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'target-to-romanization',
        'romanization-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: chineseBeginnerItems
  },
  intermediate: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'chinese',
      level: 'intermediate',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'target-to-romanization',
        'romanization-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: chineseIntermediateItems
  },
  advanced: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'chinese',
      level: 'advanced',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'target-to-romanization',
        'romanization-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: chineseAdvancedItems
  }
};

// Russian Dictionaries
export const russianVocabulary: Record<VocabularyLevel, MultipleChoiceDictionary> = {
  beginner: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'russian',
      level: 'beginner',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'target-to-romanization',
        'romanization-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: russianBeginnerItems
  },
  intermediate: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'russian',
      level: 'intermediate',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'target-to-romanization',
        'romanization-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: russianIntermediateItems
  },
  advanced: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'russian',
      level: 'advanced',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'target-to-romanization',
        'romanization-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: russianAdvancedItems
  }
};

// German Dictionaries (no romanization)
export const germanVocabulary: Record<VocabularyLevel, MultipleChoiceDictionary> = {
  beginner: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'german',
      level: 'beginner',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: germanBeginnerItems
  },
  intermediate: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'german',
      level: 'intermediate',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: germanIntermediateItems
  },
  advanced: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'german',
      level: 'advanced',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: germanAdvancedItems
  }
};

// Latin Dictionaries (no romanization)
export const latinVocabulary: Record<VocabularyLevel, MultipleChoiceDictionary> = {
  beginner: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'latin',
      level: 'beginner',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: latinBeginnerItems
  },
  intermediate: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'latin',
      level: 'intermediate',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: latinIntermediateItems
  },
  advanced: {
    metadata: {
      matchingMode: 'multiple-choice',
      language: 'latin',
      level: 'advanced',
      supportedModes: [
        'target-to-english',
        'english-to-target',
        'definition-to-target'
      ],
      totalItems: 50,
      version: '1.0.0'
    },
    items: latinAdvancedItems
  }
};

// Combined vocabulary data
export const vocabularyData: Record<SupportedLanguage, Record<VocabularyLevel, MultipleChoiceDictionary> | null> = {
  chinese: chineseVocabulary,
  russian: russianVocabulary,
  german: germanVocabulary,
  latin: latinVocabulary
};

/**
 * Get vocabulary by language and level
 */
export function getVocabulary(
  language: SupportedLanguage,
  level: VocabularyLevel
): MultipleChoiceDictionary | null {
  const langData = vocabularyData[language];
  if (!langData) return null;
  return langData[level] || null;
}

/**
 * Get vocabulary items by language and level
 */
export function getVocabularyItems(
  language: SupportedLanguage,
  level: VocabularyLevel
): VocabularyItem[] {
  const dictionary = getVocabulary(language, level);
  return dictionary?.items || [];
}

/**
 * Check if vocabulary is available for a language/level
 */
export function isVocabularyAvailable(
  language: SupportedLanguage,
  level?: VocabularyLevel
): boolean {
  const langData = vocabularyData[language];
  if (!langData) return false;

  if (level) {
    const vocab = langData[level];
    return vocab !== null && vocab.items.length > 0;
  }

  // Check if any level is available
  return Object.values(langData).some(v => v !== null && v.items.length > 0);
}

/**
 * Get supported question types for a language
 */
export function getSupportedQuestionTypes(language: SupportedLanguage): QuestionType[] {
  const langData = vocabularyData[language];
  if (!langData) return [];

  // Get from first available level
  for (const level of ['beginner', 'intermediate', 'advanced'] as VocabularyLevel[]) {
    const vocab = langData[level];
    if (vocab) {
      return vocab.metadata.supportedModes;
    }
  }

  return [];
}

/**
 * Get total item count for a language
 */
export function getTotalItemCount(language: SupportedLanguage): number {
  const langData = vocabularyData[language];
  if (!langData) return 0;

  let total = 0;
  for (const level of ['beginner', 'intermediate', 'advanced'] as VocabularyLevel[]) {
    const vocab = langData[level];
    if (vocab) {
      total += vocab.items.length;
    }
  }

  return total;
}

/**
 * Get all available languages
 */
export function getAvailableLanguages(): SupportedLanguage[] {
  return (Object.keys(vocabularyData) as SupportedLanguage[]).filter(
    lang => isVocabularyAvailable(lang)
  );
}

/**
 * Get all items across all levels for a language (for distractor generation)
 */
export function getAllItemsForLanguage(language: SupportedLanguage): VocabularyItem[] {
  const langData = vocabularyData[language];
  if (!langData) return [];

  const allItems: VocabularyItem[] = [];
  for (const level of ['beginner', 'intermediate', 'advanced'] as VocabularyLevel[]) {
    const vocab = langData[level];
    if (vocab) {
      allItems.push(...vocab.items);
    }
  }

  return allItems;
}
